import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  ChevronUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

export default function LetteraCcnlAttachmentsSelect({
  visible = false,
  token,
  getCCNLListPaginated,
  defaultCcnl = null,
  value = null,
  onChange,
  onSaveDefault,
  savingDefault = false,
  title = "CCNL per estrazione allegato trasparenza",
  label = "Seleziona il CCNL da usare per l’estrazione degli allegati",
  placeholder = "Seleziona un CCNL",
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const NONE_ATTACHMENT_ID = 0;

  const rootRef = useRef(null);
  const listRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const defaultCcnlId = defaultCcnl?.id ?? null;
  const defaultCcnlCode = defaultCcnl?.codice ?? "";
  const defaultCcnlLabel = defaultCcnl?.nome ?? "";

  const selectedId = useMemo(() => {
    if (value !== null && value !== undefined && value !== "") {
      return Number(typeof value === "object" ? value?.id : value);
    }
    if (defaultCcnlId !== null && defaultCcnlId !== undefined && defaultCcnlId !== "") {
      return Number(defaultCcnlId);
    }
    return null;
  }, [value, defaultCcnlId]);

  const isNoneSelected = selectedId === NONE_ATTACHMENT_ID;

  const normalizeRows = useCallback((response) => {
    const rows = Array.isArray(response)
      ? response
      : Array.isArray(response?.results)
      ? response.results
      : Array.isArray(response?.data)
      ? response.data
      : [];

    return rows
      .map((item) => ({
        id: item?.id ?? item?.pk ?? null,
        codice: String(item?.codice ?? "").trim(),
        nome: String(
          item?.nome ??
            item?.descrizione ??
            item?.label ??
            item?.text ??
            ""
        ).trim(),
      }))
      .filter((item) => item.id && item.codice);
  }, []);

  const mergeUnique = useCallback((prev, next) => {
    const map = new Map();

    [...prev, ...next].forEach((item) => {
      map.set(Number(item.id), item);
    });

    return Array.from(map.values());
  }, []);

  const fetchPage = useCallback(
    async ({ pageToLoad = 1, query = "", append = false }) => {
      if (
        !visible ||
        !token ||
        typeof getCCNLListPaginated !== "function"
      ) {
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await getCCNLListPaginated(token, query || null, pageToLoad, 10);
        const normalized = normalizeRows(response);

        setOptions((previous) => append ? mergeUnique(previous, normalized) : normalized);
        setPage(pageToLoad);

        const nextExists =
          Boolean(response?.next) ||
          (
            Array.isArray(response?.results) &&
            response.results.length === 10
          );

        setHasMore(nextExists);
      } catch (error) {
        console.error(
          "Errore caricamento lista CCNL:",
          error
        );

        if (!append) {
          setOptions([]);
        }

        setHasMore(false);
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [visible, token, getCCNLListPaginated, normalizeRows, mergeUnique]
  );

  useEffect(() => {
    if (!visible) return;
    fetchPage({ pageToLoad: 1, query: "", append: false });
  }, [visible, fetchPage]);

  useEffect(() => {
    if (!visible) return;
    if (!defaultCcnlId) return;
    if (value !== null && value !== undefined && value !== "") return;

    onChange?.({
      id: Number(defaultCcnlId),
      codice: defaultCcnlCode || "",
      nome: defaultCcnlLabel || "",
    });
  }, [visible, defaultCcnlId, defaultCcnlCode, defaultCcnlLabel, value, onChange]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!visible) return;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchPage({
        pageToLoad: 1,
        query: search.trim(),
        append: false,
      });
    }, 250);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search, visible, fetchPage]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingMore || loading || !hasMore) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom) {
      fetchPage({
        pageToLoad: page + 1,
        query: search.trim(),
        append: true,
      });
    }
  }, [fetchPage, hasMore, loading, loadingMore, page, search]);

  const selectedOption = useMemo(() => {
    if (value && typeof value === "object") return value;
    return options.find((item) => Number(item.id) === Number(selectedId)) || null;
  }, [options, selectedId, value]);

  const isSelectedDefault = useMemo(() => {
    if (selectedId === NONE_ATTACHMENT_ID) return false;
    return selectedId !== null && Number(selectedId) === Number(defaultCcnlId);
  }, [selectedId, defaultCcnlId]);

  const buttonLabel = useMemo(() => {
    if (isNoneSelected) {
      return "Nessun allegato";
    }

    if (selectedOption?.codice) {
      return `${selectedOption.codice}${selectedOption.nome ? ` - ${selectedOption.nome}` : ""}`;
    }

    if (defaultCcnlCode) {
      return `${defaultCcnlCode}${defaultCcnlLabel ? ` - ${defaultCcnlLabel}` : ""}`;
    }

    return placeholder;
  }, [isNoneSelected, selectedOption, defaultCcnlCode, defaultCcnlLabel, placeholder]);

  const defaultExists = defaultCcnlId !== null;

  const defaultAction = useMemo(() => {
    // Nessun default configurato
    if (!defaultExists) {
      if (
        selectedId !== null &&
        selectedId !== NONE_ATTACHMENT_ID
      ) {
        return "save";
      }
      return null;
    }

    // Esiste un default

    // Se è selezionato il default oppure "Nessun allegato"
    if (
      selectedId === NONE_ATTACHMENT_ID ||
      Number(selectedId) === Number(defaultCcnlId)
    ) {
      return "remove";
    }

    // È selezionato un altro CCNL
    return "update";
  }, [
    selectedId,
    defaultCcnlId,
    defaultExists,
  ]);

  const handlePick = (item) => {
    onChange?.({
      id: item?.id ?? null,
      codice: item?.codice ?? "",
      nome: item?.nome ?? "",
    });
    setOpen(false);
  };

  const handleClear = () => {
    setSearch("");
    onChange?.({
      id: NONE_ATTACHMENT_ID,
      codice: "",
      nome: "Nessun allegato",
    });
    setOpen(false);
  };

  const handleSaveDefault = () => {
    if (selectedId === NONE_ATTACHMENT_ID) {
      onSaveDefault?.(null);
      return;
    }

    if (!selectedOption) return;

    onSaveDefault?.({
      id: selectedOption.id,
      codice: selectedOption.codice,
      nome: selectedOption.nome,
    });
  };

  if (!visible) return null;

  return (
    <fieldset className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-4 rounded-md bg-indigo-50 py-2 pr-2">
        <legend className="flex items-center gap-2">
          <span className="inline-block h-6 w-1 rounded bg-gradient-to-b from-indigo-200 to-indigo-400" />
          <span className="text-sm font-semibold text-gray-900">{title}</span>
        </legend>

        {defaultAction && (
          <button
            type="button"
            disabled={savingDefault}
            onClick={() => {
              if (defaultAction === "remove") {
                onSaveDefault?.(null);
              } else {
                handleSaveDefault();
              }
            }}
            className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
          >
            {savingDefault
              ? "Salvataggio..."
              : defaultAction === "save"
              ? "Salva come predefinito"
              : defaultAction === "update"
              ? "Aggiorna CCNL predefinito"
              : "Rimuovi CCNL predefinito"}
          </button>
        )}
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-gray-700">
            {label}
            <span className="ml-2 text-gray-600 font-normal text-xs">
              - se non è presente, scrivici a supporto@consultala.com per richiedere l'implementazione
            </span>
          </span>

          <div ref={rootRef} className="relative">
            <div className="relative mb-2">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!open) setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                placeholder="Cerca per codice o descrizione..."
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <button
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              disabled={loading}
              className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-700 shadow-sm transition hover:border-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className={selectedId !== null && selectedId !== undefined ? "truncate text-gray-900" : "truncate text-gray-400"}>
                  {loading ? "Caricamento CCNL..." : buttonLabel}
                </span>

                {isSelectedDefault ? (
                  <span className="inline-flex flex-none items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                    Predefinito
                  </span>
                ) : null}
              </div>

              <ChevronUpDownIcon className="h-5 w-5 flex-none text-gray-400" />
            </button>

            {open && (
              <div
                ref={listRef}
                onScroll={handleScroll}
                className="absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg"
              >
                <button
                  type="button"
                  onClick={handleClear}
                  className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
                >
                  Nessun allegato
                </button>

                {options.length ? (
                  options.map((item) => {
                    const isSelected = Number(item.id) === Number(selectedId);
                    const isDefault = Number(item.id) === Number(defaultCcnlId);

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handlePick(item)}
                        className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-indigo-50"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">
                              {item.codice}
                            </div>

                            {isDefault ? (
                              <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                Predefinito
                              </span>
                            ) : null}
                          </div>

                          {item.nome ? (
                            <div className="truncate text-xs text-gray-500">
                              {item.nome}
                            </div>
                          ) : null}
                        </div>

                        {isSelected ? (
                          <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-indigo-600" />
                        ) : null}
                      </button>
                    );
                  })
                ) : !loading ? (
                  <div className="px-3 py-3 text-sm text-gray-500">
                    Nessun CCNL trovato
                  </div>
                ) : null}

                {loadingMore ? (
                  <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                    Caricamento altri CCNL...
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </label>
      </div>
      {selectedId !== null &&
        selectedId !== NONE_ATTACHMENT_ID &&
        Number(selectedId) !== Number(defaultCcnlId) && (
          <span className="mt-2 block text-xs font-medium text-gray-700">
            Seleziona{" "}
            <span className="text-indigo-700">
              “Salva come predefinito” / “Aggiorna”
            </span>{" "}
            per impostare la selezione automatica
          </span>
      )}
    </fieldset>
  );
}
